import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Download, Star, Trash2, ClipboardList, Loader2, X, Bell } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BUDGET_BANDS } from "@/lib/causes";
import { VerificationBadge } from "@/components/VerificationBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function CSRWorkspace() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [budget, setBudget] = useState("");
  const [selected, setSelected] = useState([]);
  const [shortlists, setShortlists] = useState([]);
  const [events, setEvents] = useState([]);
  const [searches, setSearches] = useState([]);
  const [name, setName] = useState("");
  const [pdfId, setPdfId] = useState(null);

  const load = () => {
    const qp = new URLSearchParams({ csr_eligible: "true" });
    if (budget) qp.set("budget", budget);
    api.get(`/orgs?${qp}`).then((r) => setOrgs(r.data.orgs || []));
  };
  useEffect(load, [budget]);
  useEffect(() => {
    api.get("/shortlists").then((r) => setShortlists(r.data.shortlists || []));
    api.get("/csr/contact-log").then((r) => setEvents(r.data.events || []));
    api.get("/saved-searches").then((r) => setSearches(r.data.searches || []));
  }, []);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : s.length >= 5 ? (toast.error("Compare up to 5"), s) : [...s, id]);
  const chosen = orgs.filter((o) => selected.includes(o.id));

  const saveShortlist = async () => {
    if (!name || selected.length === 0) { toast.error("Name it and pick at least one NGO"); return; }
    const { data } = await api.post("/shortlists", { name, org_ids: selected });
    setShortlists((s) => [...s, data]); setName(""); setSelected([]);
    toast.success("Shortlist saved");
  };
  const delShortlist = async (id) => { await api.delete(`/shortlists/${id}`); setShortlists((s) => s.filter((x) => x.id !== id)); };
  const delSearch = async (id) => { await api.delete(`/saved-searches/${id}`); setSearches((s) => s.filter((x) => x.id !== id)); };

  const downloadPack = async (id, dn) => {
    setPdfId(id);
    try {
      const res = await api.get(`/orgs/${id}/due-diligence`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `due-diligence-${dn}.pdf`; a.click();
      toast.success("Pack downloaded");
    } catch (e) { toast.error("Failed"); } finally { setPdfId(null); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-brand">CSR workspace</h1>
      <p className="text-sm text-stone-500">{user?.org_name || "Corporate account"} · showing statutorily eligible partners only</p>

      <Tabs defaultValue="match" className="mt-6">
        <TabsList data-testid="csr-tabs">
          <TabsTrigger value="match" data-testid="tab-match">Budget matching</TabsTrigger>
          <TabsTrigger value="compare" data-testid="tab-compare">Compare ({selected.length})</TabsTrigger>
          <TabsTrigger value="shortlists" data-testid="tab-shortlists">Shortlists ({shortlists.length})</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">Saved &amp; alerts ({searches.length})</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">Contact log</TabsTrigger>
        </TabsList>

        <TabsContent value="match" className="mt-5">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm font-medium text-stone-600">Budget band:</span>
            <select value={budget} onChange={(e) => setBudget(e.target.value)} className="rounded-lg border border-stone-300 px-3 py-2 text-sm" data-testid="csr-budget-select">
              <option value="">Any</option>
              {BUDGET_BANDS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
            </select>
            {selected.length > 0 && (
              <div className="ml-auto flex items-center gap-2">
                <Input placeholder="Shortlist name" value={name} onChange={(e) => setName(e.target.value)} className="w-44" data-testid="shortlist-name-input" />
                <Button size="sm" onClick={saveShortlist} className="bg-terracotta hover:bg-terracotta-hover" data-testid="save-shortlist-btn"><Star size={15} className="mr-1" /> Save shortlist</Button>
              </div>
            )}
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {orgs.map((o) => (
              <div key={o.id} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                <div className="relative aspect-video">
                  <img src={o.hero_image} alt={o.display_name} className="w-full h-full object-cover" />
                  <label className="absolute top-2 right-2 bg-white/95 rounded-md px-2 py-1 text-xs font-medium flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} data-testid={`csr-compare-checkbox-${o.id}`} /> Compare
                  </label>
                  <div className="absolute top-2 left-2"><VerificationBadge tier={o.verification_tier} /></div>
                </div>
                <div className="p-4">
                  <Link to={`/ngo/${o.id}`} className="font-bold text-stone-900 hover:text-terracotta">{o.display_name}</Link>
                  <p className="text-xs text-stone-500 mt-0.5">{o.district} · {BUDGET_BANDS.find((b) => b.id === o.budget_band)?.label}</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="text-xs bg-tier2-bg text-tier2-text border border-tier2-border rounded-full px-2 py-0.5 font-semibold flex items-center gap-1"><Check size={11} /> CSR-eligible</span>
                    <span className="text-xs text-stone-500">{o.credentials_summary?.join(" · ")}</span>
                  </div>
                  <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => downloadPack(o.id, o.display_name)} disabled={pdfId === o.id} data-testid={`csr-pdf-${o.id}`}>
                    {pdfId === o.id ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />} Due-diligence pack
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="compare" className="mt-5">
          {chosen.length === 0 ? <p className="text-stone-500">Select NGOs from Budget matching to compare side by side (up to 5).</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-stone-200 rounded-xl overflow-hidden bg-white">
                <tbody>
                  <Cmp label="Organisation" cells={chosen.map((o) => <Link key={o.id} to={`/ngo/${o.id}`} className="font-semibold text-slate-brand">{o.display_name}</Link>)} />
                  <Cmp label="Tier" cells={chosen.map((o) => <VerificationBadge key={o.id} tier={o.verification_tier} />)} />
                  <Cmp label="District" cells={chosen.map((o) => o.district)} />
                  <Cmp label="Budget band" cells={chosen.map((o) => BUDGET_BANDS.find((b) => b.id === o.budget_band)?.label)} />
                  <Cmp label="Beneficiaries" cells={chosen.map((o) => o.beneficiary_count?.toLocaleString())} />
                  <Cmp label="Credentials" cells={chosen.map((o) => o.credentials_summary?.join(", "))} />
                  <Cmp label="Schedule VII" cells={chosen.map((o) => (o.schedule_vii_codes || []).join(", "))} />
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="shortlists" className="mt-5 space-y-3">
          {shortlists.length === 0 ? <p className="text-stone-500">No shortlists yet.</p> : shortlists.map((s) => (
            <div key={s.id} className="bg-white rounded-xl border border-stone-200 p-4 flex items-center justify-between" data-testid={`shortlist-${s.id}`}>
              <div><div className="font-semibold text-stone-900">{s.name}</div><div className="text-xs text-stone-500">{s.org_ids.length} organisations</div></div>
              <Button variant="ghost" size="icon" onClick={() => delShortlist(s.id)} data-testid={`delete-shortlist-${s.id}`}><Trash2 size={16} className="text-red-500" /></Button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="alerts" className="mt-5 space-y-3">
          <p className="text-sm text-stone-500">Save a filter on the Discover page. When a newly-verified eligible partner matches it, the count below rises.</p>
          {searches.length === 0 ? <p className="text-stone-500">No saved searches yet.</p> : searches.map((s) => (
            <div key={s.id} className="bg-white rounded-xl border border-stone-200 p-4 flex items-center justify-between" data-testid={`saved-search-${s.id}`}>
              <div>
                <div className="font-semibold text-stone-900 flex items-center gap-2">
                  <Bell size={15} className="text-gold" /> {s.name}
                  {s.new_since_saved > 0 && <span className="text-xs bg-gold text-slate-brand rounded-full px-2 py-0.5 font-bold">{s.new_since_saved} new</span>}
                </div>
                <div className="text-xs text-stone-500 mt-0.5">{s.match_count} matching eligible partners{s.params?.cause ? ` · ${s.params.cause}` : ""}{s.params?.district ? ` · ${s.params.district}` : ""}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => delSearch(s.id)} data-testid={`delete-search-${s.id}`}><Trash2 size={16} className="text-red-500" /></Button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="log" className="mt-5">
          {events.length === 0 ? <p className="text-stone-500 flex items-center gap-2"><ClipboardList size={16} /> No contact events yet. They appear when your team reveals an NGO's contact details.</p> : (
            <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100">
              {events.map((e) => (
                <div key={e.id} className="p-4 flex items-center justify-between text-sm" data-testid={`contact-event-${e.id}`}>
                  <span className="font-medium text-stone-800">{e.org_name}</span>
                  <span className="text-stone-500">{new Date(e.occurred_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Cmp({ label, cells }) {
  return (
    <tr className="border-b border-stone-100">
      <th className="text-left p-3 bg-stone-50 font-semibold text-stone-600 w-40 align-top">{label}</th>
      {cells.map((c, i) => <td key={i} className="p-3 align-top">{c}</td>)}
    </tr>
  );
}
